import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Avatar, CircularProgress, Typography } from '@mui/material';
import Stripe from 'stripe';
import dayjs from 'dayjs';
import { basicNoTrialOfferingId, basicPriceUp202512OfferingId, Discount } from '@demind-inc/core';
import { LoadingButton } from '@mui/lab';
import { useSetRecoilState } from 'recoil';
import { useNavigate } from 'react-router-dom';
import CheckIcon from '../../../assets/onboarding/check.png';
import InfoIcon from '../../../assets/onboarding/infoIcon.svg';
import {
  DeleteAccountDialog,
  DiscountCodeDialog,
  SubscriptionPlanItem,
  ScrollTooltip,
  translatePlanPriceDetail,
  trialSteps,
  expiredTrialSteps,
} from '../../../components';
import { planContentItems } from './constants';
import {
  DEFAULT_FREE_TRIAL_DAYS,
  getFirstOnboardingStepsAfterPaywall,
  MONTH_FREE_TRIAL_DAYS,
  useAuthContext,
  useAvailableSubscriptions,
  useSubscriptionContext,
  useVerifyDiscountCode,
  useCheckoutPayment,
  useSyncSuccessStripePayment,
  generalSnackBarAtom,
} from '../../../data-access';
import { isDesktopApp, useFeatureFlag, useQueryString } from '../../../utils';
import {
  calculateOriginalPriceFromDiscount,
  calculateYearlyDiscountPercentage,
  getFreeTrialDaysFromPlan,
  sortPlans,
} from '../../../helpers';

import './PaywallPage.scss';
import LifestackLogoName from '../../../assets/logo/new_logo_with_name.png';
import { ReactComponent as StartTrialIcon } from '../../../assets/paywall/startTrial.svg';
import { ONBOARDING_PATH, Path } from '../../../routing';
import { trackEventMixpanel } from '../../../utils';
import {
  FeatureCarousel,
  PromoCard,
  TargetPlanType,
  TrialTimeline,
} from '../../../components/Subscription';

const DESKTOP_CHECKOUT_WEB_ORIGIN =
  process.env.REACT_APP_WEB_APP_URL?.trim() || 'https://app.lifestack.ai';

const openPrivacyPolicy = () => {
  window.open(
    'https://demind-inc.notion.site/Privacy-Policy-Lifestack-867d4c361ca744e0a01be8645e951998?pvs=4',
    '_blank'
  );
};

const PaywallPage: React.FC = () => {
  const [selectedPlan, setSelectedPlan] = useState<Stripe.Price | undefined>();
  const [appliedDiscount, setAppliedDiscount] = useState<Discount | null>(null);
  const [deleteUserDialogVisible, setDeleteUserDialogVisible] = useState(false);
  const [isDiscountCodeDialogOpen, setIsDiscountCodeDialogOpen] = useState(false);
  const [isDesktopCheckoutInProgress, setIsDesktopCheckoutInProgress] = useState(false);
  const navigate = useNavigate();
  const setGeneralSnackbar = useSetRecoilState(generalSnackBarAtom);
  const contentRef = useRef<HTMLDivElement>(null);
  const desktopApp = isDesktopApp();

  const {
    featureFlags: { FF_30_day_trial = false },
  } = useFeatureFlag();
  const { availableSubscriptions, isLoading: isFetchingPlans } = useAvailableSubscriptions({
    provider: 'stripe',
    access: 'basic_price_up_202512',
  }); // Web supports only Stripe payment.

  const { signOut, user } = useAuthContext();
  const {
    rewardfulCaompaignId,
    rewardfulCustomerId,
    hasExpiredSubscription,
    hasSkipCardSubscription,
    isRequestingSubscription,
    requestSkipCardSubscription,
    requestFreeSubscription,
    refetchSubscriptions,
  } = useSubscriptionContext();
  const {
    verifyDiscountCode,
    isPending: isVerifyingDiscountCode,
    error: verifyDiscountError,
  } = useVerifyDiscountCode();
  const { queryString: referralQSParam } = useQueryString({ qskey: 'via' });
  const { checkoutPayment, isPending: isCheckoutLoading } = useCheckoutPayment();
  const { syncSuccessStripePayment, isPending: isSyncingStripeSuccess } =
    useSyncSuccessStripePayment();

  const selectedPlanRecurring: TargetPlanType = useMemo(() => {
    if (!selectedPlan) {
      return 'yearly';
    }
    const isLifetimePlan = selectedPlan.lookup_key?.includes('basic_lifetime');

    if (isLifetimePlan) {
      return 'lifetime';
    }
    if (selectedPlan.recurring.interval === 'month') {
      return 'monthly';
    }
    if (selectedPlan.recurring.interval === 'year') {
      return 'yearly';
    }
  }, [selectedPlan]);

  const filteredAvailableSubscriptions = useMemo(() => {
    // If the discount is a percentage discount, we need to filter the available subscriptions based on the offering ID
    const targetOfferingIds =
      appliedDiscount?.discountType === 'percentage'
        ? appliedDiscount?.applicableOfferingIds || [
            basicPriceUp202512OfferingId,
            basicNoTrialOfferingId,
          ]
        : [basicPriceUp202512OfferingId, basicNoTrialOfferingId];
    const applicablePlanTypes = appliedDiscount?.applicablePlanTypes;

    return availableSubscriptions.filter((plan) => {
      if (!targetOfferingIds.includes(plan.metadata.offeringId)) {
        return false;
      }
      if (!appliedDiscount || !applicablePlanTypes?.length) {
        return true;
      }
      const isLifetimePlan = plan.lookup_key?.includes('basic_lifetime');
      const planType = isLifetimePlan
        ? 'LIFETIME'
        : plan.recurring?.interval === 'month'
          ? 'MONTHLY'
          : plan.recurring?.interval === 'year'
            ? 'ANNUAL'
            : undefined;

      return planType ? applicablePlanTypes.includes(planType) : false;
    });
  }, [availableSubscriptions, appliedDiscount]);

  const offeringId = useMemo(
    () =>
      appliedDiscount?.applicableOfferingIds[0] ||
      (hasSkipCardSubscription ? basicNoTrialOfferingId : basicPriceUp202512OfferingId),
    [appliedDiscount, hasSkipCardSubscription]
  );

  // Apply the referral code via rewardful
  useEffect(() => {
    if (!rewardfulCaompaignId) {
      return;
    }

    const init = async () => {
      const discount = await verifyDiscountCode({
        code: rewardfulCaompaignId,
      });
      setAppliedDiscount(discount);
    };
    init();
  }, [rewardfulCaompaignId]);

  useEffect(() => {
    if (!referralQSParam) {
      return;
    }
    // Update the URL with the referral code
    const currentUrl = new URL(window.location.href);
    currentUrl.searchParams.set('via', referralQSParam);
    window.history.pushState({}, '', currentUrl.href);

    // Reload the page to ensure to apply the referral code
    const hasReloaded = JSON.parse(localStorage.getItem('hasReloaded') || 'false') as boolean;
    if (hasReloaded) {
      return;
    }
    localStorage.setItem('hasReloaded', 'true');
    setTimeout(() => window.location.reload(), 1000);
  }, [referralQSParam]);

  // Set the default plan to annual plan
  useEffect(() => {
    if (!filteredAvailableSubscriptions.length) {
      return;
    }

    setSelectedPlan(
      filteredAvailableSubscriptions.find(({ recurring }) => recurring?.interval === 'year')
    );
  }, [filteredAvailableSubscriptions]);

  const freeTrialDays = useMemo(() => {
    const isLifetimePlan = selectedPlan?.lookup_key?.includes('basic_lifetime');
    if (isLifetimePlan) {
      return 0;
    }
    if (hasExpiredSubscription) {
      return 0;
    }
    if (appliedDiscount && appliedDiscount.trialDays) {
      return appliedDiscount.trialDays;
    }
    if (FF_30_day_trial) {
      return MONTH_FREE_TRIAL_DAYS;
    }
    if (hasSkipCardSubscription) {
      return 0;
    }
    if (!selectedPlan) {
      return DEFAULT_FREE_TRIAL_DAYS;
    }
    // Get free trial days from plan metadata
    return getFreeTrialDaysFromPlan(selectedPlan);
  }, [
    selectedPlan,
    hasSkipCardSubscription,
    appliedDiscount,
    FF_30_day_trial,
    hasExpiredSubscription,
  ]);

  const showsTrialHeading =
    selectedPlanRecurring === 'yearly' && freeTrialDays > 0 && !hasExpiredSubscription;

  const handleSignOut = () => {
    signOut();
  };

  const isFreePlan = useMemo(() => appliedDiscount?.discountType === 'free', [appliedDiscount]);

  const navigateAfterSuccessfulPurchase = useCallback(() => {
    const hasCompletedAfterPaywall = !!user?.onboarding?.afterPaywall;
    if (hasCompletedAfterPaywall) {
      navigate(`${Path.DASHBOARD}?subscriptionSuccess=true`, { replace: true });
      return;
    }

    const nextPath = ONBOARDING_PATH[getFirstOnboardingStepsAfterPaywall()] || Path.DASHBOARD;
    navigate(`${nextPath}?subscriptionSuccess=true`, { replace: true });
  }, [navigate, user?.onboarding?.afterPaywall]);

  const applyDiscountCode = async (code: string) => {
    const discount = await verifyDiscountCode({
      code,
      userId: user?.userId,
    });
    setAppliedDiscount(discount);
  };

  const handleRequestFreeSubscription = async () => {
    const nextPath = ONBOARDING_PATH[getFirstOnboardingStepsAfterPaywall()] || Path.DASHBOARD;

    const onPurchaseDone = () =>
      navigate(`${nextPath}?subscriptionSuccess=true`, {
        replace: true,
      });
    if (appliedDiscount?.trialDays) {
      await requestSkipCardSubscription({
        trialDays: appliedDiscount.trialDays,
        onPurchaseDone: onPurchaseDone,
        discountId: appliedDiscount.discountId,
        offeringId: offeringId,
      });
    } else {
      // if no trialDays, forever free
      await requestFreeSubscription(onPurchaseDone);
    }
  };

  const handleStripeCheckout = async () => {
    if (!selectedPlan || !user?.userId || !user?.email) {
      return;
    }

    let didCompleteDesktopCheckout = false;

    try {
      const isLifetimePlan = selectedPlan.lookup_key?.includes('basic_lifetime');
      const plan = isLifetimePlan
        ? 'lifetime'
        : selectedPlan.recurring?.interval === 'month'
          ? 'monthly'
          : selectedPlan.recurring?.interval === 'year'
            ? 'yearly'
            : 'weekly';

      let successUrl: string;
      let cancelUrl: string;

      if (desktopApp) {
        // Packaged desktop builds run on file://, so Stripe callbacks need a hosted origin.
        const checkoutBaseUrl = DESKTOP_CHECKOUT_WEB_ORIGIN;
        const normalizedCheckoutBaseUrl = checkoutBaseUrl.endsWith('/')
          ? checkoutBaseUrl
          : `${checkoutBaseUrl}/`;
        const desktopSuccessUrl = new URL(
          Path.STRIPE_CHECKOUT.SUCCESS,
          normalizedCheckoutBaseUrl
        );

        successUrl = `${desktopSuccessUrl.toString()}?session_id={CHECKOUT_SESSION_ID}`;
        cancelUrl = new URL(Path.PAYWALL, normalizedCheckoutBaseUrl).toString();
      } else {
        // Keep web/mobile checkout URL construction identical to the existing web flow.
        const successUrlParams = new URLSearchParams();
        if (rewardfulCaompaignId && rewardfulCustomerId) {
          successUrlParams.set('referral_campaign_id', rewardfulCaompaignId);
          successUrlParams.set('rewardful_client_id', rewardfulCustomerId);
        }
        if (appliedDiscount) {
          successUrlParams.set('discount', encodeURIComponent(JSON.stringify(appliedDiscount)));
        }
        successUrl = `${window.location.origin}${Path.STRIPE_CHECKOUT.SUCCESS}?session_id={CHECKOUT_SESSION_ID}&${successUrlParams.toString()}`;
        cancelUrl = `${window.location.origin}${Path.PAYWALL}`;
      }

      const { checkoutUrl, sessionId } = await checkoutPayment({
        userId: user.userId,
        email: user.email,
        priceId: selectedPlan.id,
        plan,
        offeringId,
        successUrl,
        cancelUrl,
        freeTrialDays: freeTrialDays,
        ...(!!rewardfulCaompaignId && !!rewardfulCustomerId
          ? {
              referral: {
                campaignId: rewardfulCaompaignId,
                rewardfulClientId: rewardfulCustomerId,
              },
            }
          : {}),
        ...(appliedDiscount ? { discount: appliedDiscount } : {}),
      });

      trackEventMixpanel('stripe_checkout_initiated', {
        from: desktopApp ? 'desktop' : 'web',
        offeringId,
        plan,
        priceId: selectedPlan.id,
      });

      if (desktopApp) {
        if (!window.lifestack?.startStripeDesktopCheckout) {
          throw new Error('desktop_checkout_unavailable');
        }

        setIsDesktopCheckoutInProgress(true);
        try {
          const checkoutResult = await window.lifestack.startStripeDesktopCheckout({
            checkoutUrl,
            successUrl,
            cancelUrl,
          });

          if (checkoutResult.status !== 'success') {
            setGeneralSnackbar('Checkout cancelled.');
            return;
          }

          didCompleteDesktopCheckout = true;
          const completedSessionId = checkoutResult.sessionId || sessionId;
          if (!completedSessionId) {
            throw new Error('desktop_checkout_missing_session_id');
          }

          await syncSuccessStripePayment({
            userId: user.userId,
            sessionId: completedSessionId,
            ...(!!rewardfulCaompaignId && !!rewardfulCustomerId
              ? {
                  referral: {
                    campaignId: rewardfulCaompaignId,
                    rewardfulClientId: rewardfulCustomerId,
                  },
                }
              : {}),
            ...(appliedDiscount ? { discount: appliedDiscount } : {}),
          });
          await refetchSubscriptions();
          navigateAfterSuccessfulPurchase();
        } finally {
          setIsDesktopCheckoutInProgress(false);
        }
        return;
      }

      window.location.href = checkoutUrl;
    } catch (error) {
      console.error('Error initiating Stripe Checkout:', error);
      if (desktopApp) {
        const message =
          error instanceof Error && error.message === 'stripe_desktop_checkout_timeout'
            ? 'Checkout timed out. Please try again.'
            : didCompleteDesktopCheckout
              ? 'Payment completed but we could not update your subscription. Please contact support.'
              : 'Checkout failed. Please try again.';
        setGeneralSnackbar(message);
      }
    }
  };

  const sortedPlans = useMemo(
    () => sortPlans([...filteredAvailableSubscriptions]),
    [filteredAvailableSubscriptions]
  );

  const selectedPlanPriceCopy = useMemo(() => {
    if (isFreePlan) {
      if (appliedDiscount?.trialDays) {
        return `$0 for ${appliedDiscount.trialDays} days`;
      }
      return 'Free forever plan applied';
    }
    if (!selectedPlan) {
      return '';
    }
    const price = translatePlanPriceDetail(selectedPlan, true);
    if (selectedPlan.lookup_key?.includes('basic_lifetime')) {
      return `${price} charged once`;
    }
    if (freeTrialDays) {
      return `${freeTrialDays}-day trial, then ${price}`;
    }
    return `${price} billed every ${selectedPlan.recurring?.interval}`;
  }, [appliedDiscount?.trialDays, freeTrialDays, isFreePlan, selectedPlan]);

  const getCardContent = useCallback(
    (plan: Stripe.Price) => {
      let label = '';
      let title = translatePlanPriceDetail(plan, true);
      let desc = '';
      const discountPercentage = calculateYearlyDiscountPercentage(
        filteredAvailableSubscriptions,
        plan
      );

      // Check if appliedDiscount has discountLabel and applicablePlanTypes includes this plan's recurring type
      const isLifetimePlan = plan.lookup_key?.includes('basic_lifetime');
      const recurring = plan.recurring?.interval;

      const planTypeMap: Record<string, string> = {
        week: 'WEEKLY',
        month: 'MONTHLY',
        year: 'ANNUAL',
        lifetime: 'LIFETIME',
      };

      const currentPlanTypeString = isLifetimePlan
        ? 'LIFETIME'
        : recurring
          ? planTypeMap[recurring]
          : undefined;

      const applicablePlanTypes = appliedDiscount?.applicablePlanTypes as string[] | undefined;
      const isDiscountApplicable =
        appliedDiscount?.discountLabel &&
        currentPlanTypeString &&
        applicablePlanTypes?.includes(currentPlanTypeString);

      if (isDiscountApplicable && appliedDiscount.discountLabel) {
        label = appliedDiscount.discountLabel;
      }

      // Set desc based on plan type regardless of discount
      if (recurring === 'year') {
        if (!isDiscountApplicable) {
          label = discountPercentage ? `Save ${discountPercentage.toString()}%` : '';
        }
        desc = 'Cancel anytime, billed annually';
      }
      if (recurring === 'month') {
        if (!isDiscountApplicable) {
          label = 'Popular';
        }
        desc = 'Cancel anytime, billed monthly';
      }
      if (isLifetimePlan) {
        if (!isDiscountApplicable) {
          label = 'Best value!';
        }
        desc = 'Billed once, forever access';
      }
      if (recurring === 'week') {
        if (!isDiscountApplicable) {
          label = '';
        }
        desc = 'Cancel anytime, billed weekly';
      }

      return { label, title, desc };
    },
    [filteredAvailableSubscriptions, appliedDiscount]
  );

  const highlightedPlan = selectedPlan || sortedPlans.find(Boolean) || undefined;
  const highlightDiscount = highlightedPlan
    ? calculateYearlyDiscountPercentage(filteredAvailableSubscriptions, highlightedPlan)
    : undefined;
  const featureCarouselItems = [
    {
      text: 'Works as a 24/7 copilot for daily\ndecisions.',
    },
    {
      text: 'Helps you optimize your time and\nenergy throughout the day.',
    },
    {
      text: 'Provides personalized scheduling\nbased on your rhythms.',
    },
    {
      text: 'Adapts to your lifestyle and\npreferences automatically.',
    },
  ];

  return (
    <div className="paywall-page">
      <div ref={contentRef} className="paywall-page__content">
        <header className="paywall-page__header">
          <img src={LifestackLogoName} alt="Lifestack" className="paywall-page__logo" />
        </header>

        <section className="paywall-page__hero">
          {/* <div className="paywall-page__hero-image">
            <img src={PaywallPhone} alt="Lifestack preview" />
          </div> */}
          {/* TODO: image */}

          <div className="paywall-page__trial-start">
            <div className="paywall-page__trial-start__content">
              <StartTrialIcon />
              How your {showsTrialHeading ? 'trial' : 'subscription'} works
            </div>
          </div>
        </section>

        <section className="paywall-page__timeline-panel">
          <TrialTimeline
            steps={
              isFreePlan
                ? trialSteps.lifetime
                : hasExpiredSubscription
                  ? expiredTrialSteps[selectedPlanRecurring]
                  : trialSteps[selectedPlanRecurring]
            }
          />
        </section>
        <ScrollTooltip
          className="paywall-page__scroll-tooltip"
          showText={false}
          onClick={() => contentRef.current?.scrollBy({ top: 300, behavior: 'smooth' })}
        />

        <section className="paywall-page__section paywall-page__section--center">
          <Typography variant="h5" className="paywall-page__section-title">
            Get unlimited access to your personal plan
          </Typography>
          <FeatureCarousel items={featureCarouselItems} />
          <PromoCard
            badgeText={
              highlightedPlan
                ? highlightDiscount
                  ? `Save ${highlightDiscount}%`
                  : 'Save more'
                : undefined
            }
            title={highlightedPlan ? translatePlanPriceDetail(highlightedPlan) : 'Pick a plan'}
            subtitle={
              freeTrialDays > 0
                ? 'Cancel anytime, billed after trial'
                : 'Cancel anytime, billed once'
            }
            className="paywall-page__price-highlight"
          />
        </section>

        <section className="paywall-page__section paywall-page__benefits">
          {planContentItems.map((item, idx) => (
            <div className="paywall-page__benefit" key={idx}>
              <div className="paywall-page__benefit__icon">
                <img src={CheckIcon} alt="Check" />
              </div>
              <Typography variant="body1" className="paywall-page__benefit__text">
                {item}
              </Typography>
            </div>
          ))}
        </section>

        <section className="paywall-page__section paywall-page__testimonial">
          <Avatar className="paywall-page__testimonial__avatar">K</Avatar>
          <div className="paywall-page__stars">★★★★★</div>
          <Typography variant="h5" className="paywall-page__testimonial__quote">
            "With Lifestack, tasks on my calendar match my energy levels throughout the day, and
            life couldn’t be better."
          </Typography>
          <Typography variant="body2" className="paywall-page__testimonial__name">
            Kirtis S
          </Typography>
        </section>
      </div>

      <section className="paywall-page__section paywall-page__plans-section">
        <div className="paywall-page__plans__wrapper">
          <div className="paywall-page__plans">
            {isFetchingPlans && (
              <div className="paywall-page__plans--loader">
                <CircularProgress />
              </div>
            )}
            {!isFetchingPlans &&
              (isFreePlan ? (
                <SubscriptionPlanItem
                  key={'free-plan'}
                  title={'Free plan'}
                  isSelected={true}
                  priceString={
                    appliedDiscount?.trialDays
                      ? `$0 for ${appliedDiscount.trialDays} days`
                      : '$0.00 / forever'
                  }
                  onClick={() => {}}
                  className="paywall-page__plans__item"
                />
              ) : (
                sortedPlans.map((plan) => {
                  const recurring = plan.recurring?.interval;
                  const isLifetimePlan = plan.lookup_key?.includes('basic_lifetime');
                  const planTrialDays = (() => {
                    if (isLifetimePlan) {
                      return 0;
                    }
                    if (hasExpiredSubscription) {
                      return 0;
                    }
                    if (appliedDiscount && appliedDiscount.trialDays) {
                      return appliedDiscount.trialDays;
                    }
                    return getFreeTrialDaysFromPlan(plan);
                  })();

                  const cardContent = getCardContent(plan);

                  return (
                    <SubscriptionPlanItem
                      key={plan.id}
                      title={
                        isLifetimePlan
                          ? 'Lifetime'
                          : recurring === 'week'
                            ? 'Weekly'
                            : recurring === 'month'
                              ? 'Monthly'
                              : 'Yearly'
                      }
                      isSelected={selectedPlan?.id === plan.id}
                      priceString={cardContent.title}
                      onClick={() => setSelectedPlan(plan)}
                      className="paywall-page__plans__item"
                      discountedOriginalPriceString={calculateOriginalPriceFromDiscount(
                        plan,
                        appliedDiscount?.discountPercentage
                      )}
                      customDiscountLabel={cardContent.label || undefined}
                      customDiscountLabelColor={
                        appliedDiscount?.discountLabel ? '#165B33' : undefined
                      }
                      trialDays={planTrialDays}
                    />
                  );
                })
              ))}
          </div>
        </div>

        {!!selectedPlanPriceCopy && !hasExpiredSubscription && (
          <Typography variant="body2" className="paywall-page__price-copy">
            {selectedPlanPriceCopy}
          </Typography>
        )}

        <div className="paywall-page__bottom">
          {isFreePlan ? (
            <LoadingButton
              loading={isRequestingSubscription}
              variant="contained"
              onClick={handleRequestFreeSubscription}
              fullWidth
              className="paywall-page__bottom__button"
            >
              Start for Free
            </LoadingButton>
          ) : (
            <>
              <LoadingButton
                loading={isCheckoutLoading || isSyncingStripeSuccess || isDesktopCheckoutInProgress}
                variant="contained"
                onClick={handleStripeCheckout}
                disabled={!selectedPlan}
                fullWidth
                className="paywall-page__bottom__button"
              >
                {hasExpiredSubscription
                  ? 'Resume subscription'
                  : hasSkipCardSubscription
                    ? 'Subscribe to keep using'
                    : freeTrialDays > 0
                      ? `Start ${freeTrialDays}-day free trial`
                      : 'Start now'}
              </LoadingButton>
              {/* Keep CardPayment as fallback option if needed */}
              {/* <Elements stripe={stripePromise}>
                <CardPayment
                  selectedPlan={selectedPlan}
                  freeTrialDays={freeTrialDays}
                  offeringId={offeringId}
                  discount={appliedDiscount}
                  className="paywall-page__card-payment"
                />
              </Elements> */}
            </>
          )}
          {appliedDiscount && (
            <Typography className="paywall-page__discount" style={{ color: '#680ddb' }}>
              Code applied!
            </Typography>
          )}
        </div>
        <div className="paywall-page__footer">
          <Typography
            variant="body2"
            onClick={() => setIsDiscountCodeDialogOpen(true)}
            className="paywall-page__footer__link"
          >
            Promo Code
          </Typography>
          <Typography
            variant="body2"
            onClick={handleSignOut}
            className="paywall-page__footer__link"
          >
            Logout
          </Typography>
          <Typography
            variant="body2"
            onClick={() => setDeleteUserDialogVisible(true)}
            className="paywall-page__footer__link"
          >
            Delete
          </Typography>
          <Typography
            variant="body2"
            onClick={openPrivacyPolicy}
            className="paywall-page__footer__link"
          >
            Privacy
          </Typography>
        </div>
      </section>
      <DeleteAccountDialog
        visible={deleteUserDialogVisible}
        where="beforePaywall"
        onClose={() => setDeleteUserDialogVisible(false)}
      />
      <DiscountCodeDialog
        open={isDiscountCodeDialogOpen}
        onClose={() => setIsDiscountCodeDialogOpen(false)}
        onValidate={applyDiscountCode}
        isValidating={isVerifyingDiscountCode}
        error={verifyDiscountError}
      />
    </div>
  );
};

export default PaywallPage;
